/**
 * Funktionaler HTTP-Interceptor für den X-User-ID-Workaround.
 *
 * Je nach Config-Einstellung `chatbot.authMode` wird für Chatbot-API-Requests
 * ein `X-User-ID`-Header gesetzt (JWT.sub) – Workaround für das alte
 * Dev-Backend, das keinen Bearer validiert. Das Bearer-Token-Attachment
 * übernimmt der nachgelagerte `authInterceptor()` von angular-auth-oidc-client
 * (Registrierung in app.config.ts: `withInterceptors([xUserIdInterceptor, authInterceptor()])`).
 */
import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { switchMap, take } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { NGXLogger } from 'ngx-logger';
import { ConfigService } from '../config/service/config.service';
import { KeycloakJwtPayload } from '../services/sentry-user-context.service';

/**
 * Prüft, ob der Request den X-User-ID-Header benötigt.
 * Config kann beim ersten Request (APP_INITIALIZER lädt config.json selbst
 * über die Interceptor-Kette) noch nicht geladen sein – dann nie X-User-ID.
 */
function needsUserIdHeader(url: string, configService: ConfigService): boolean {
  const chatbot = configService.configOrUndefined?.chatbot;
  if (!chatbot || (chatbot.authMode ?? 'bearer') !== 'x-user-id') {
    return false;
  }
  const base = chatbot.apiBaseUrl;
  if (!base) {
    return url.startsWith('/api/');
  }
  return url.startsWith(base);
}

/** Klont den Request mit X-User-ID aus dem JWT-`sub`; bei Decode-Fehler unverändert. */
function cloneWithUserId(
  request: HttpRequest<unknown>,
  token: string,
  logger: NGXLogger,
): HttpRequest<unknown> {
  try {
    const decoded = jwtDecode<KeycloakJwtPayload>(token);
    return request.clone({ headers: request.headers.set('X-User-ID', decoded.sub) });
  } catch (error) {
    logger.error('xUserIdInterceptor: Failed to decode access token for X-User-ID', { error });
    return request;
  }
}

/** Reichert Chatbot-API-Requests im x-user-id-Modus um den X-User-ID-Header an. */
export const xUserIdInterceptor: HttpInterceptorFn = (request, next) => {
  const configService = inject(ConfigService);

  if (!needsUserIdHeader(request.url, configService)) {
    return next(request);
  }

  const oidcSecurityService = inject(OidcSecurityService);
  const logger = inject(NGXLogger);

  return oidcSecurityService.getAccessToken().pipe(
    take(1),
    switchMap((token) => next(token ? cloneWithUserId(request, token, logger) : request)),
  );
};
